import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import LinkFieldContainer from '../../../common/components/link-field/containers/link-field-container';
import getLinkConfig from '../../../common/components/link-field/helpers/get-link-config';
import getDefaultFieldsValues from '../../../common/components/link-field/helpers/get-default-fields-values';
import getFallbackFieldsValues from '../../../common/components/link-field/helpers/get-fallback-fields-values';
import linkTypes from '../../../common/components/link-field/constants/link-types';
import fieldNames from '../../../common/components/link-field/constants/field-names';
import chunkIds from '../constants/chunk-ids';
import settingIds from '../constants/setting-ids';
import { linkFieldNames } from '../constants/field-names';
import buildLinkElementFromFieldsValues from '../helpers/build-link-element-from-fields-values';
import { getLinkFieldsValues } from '../helpers/get-fields-values';
import mediaContainerBuilder from '../helpers/media-container-builder';
import createSubmitPreProcessingLinkHandler from '../helpers/create-submit-pre-processing-link-handler';
import trackLinkSettingsFieldChanged from '../trackers/track-link-settings-field-changed';

const { ToggleSection } = designSystem;
const {
  Context: ElementSettingsContext,
  constants: { stages },
  hooks: { useMountChunk },
} = elementSettings;

const getSupportedLinkTypes = ({ isBindingEnabled }) => [
  linkTypes.PAGE,
  linkTypes.ANCHOR,
  linkTypes.EXTERNAL,
  linkTypes.PHONE,
  linkTypes.MAIL,
  linkTypes.DOWNLOAD,
  ...(isBindingEnabled ? [linkTypes.BINDING] : []),
];

const getLinkFieldsFromCache = (cachedFieldsValues) =>
  Object.fromEntries(
    Object.values(linkFieldNames).map((name) => [name, cachedFieldsValues?.[name]])
  );

const getDefaultValues = ({ linkConfig, linkElement, cachedFieldsValues }) => {
  const hasCachedLinkFields = cachedFieldsValues?.[linkFieldNames.LINK_CLICK_ACTION];

  if (hasCachedLinkFields) {
    return getLinkFieldsFromCache(cachedFieldsValues);
  }

  const defaultValues = getDefaultFieldsValues(linkConfig, linkElement);
  const hasLinkType = Boolean(defaultValues[fieldNames.LINK_TYPE]);

  if (!hasLinkType) {
    const fallbackValues = getFallbackFieldsValues(linkTypes.PAGE);

    return {
      ...defaultValues,
      ...fallbackValues,
      [linkFieldNames.LINK_CLICK_ACTION]: false,
    };
  }

  return {
    ...defaultValues,
    [linkFieldNames.LINK_CLICK_ACTION]: true,
  };
};

function LinkSettingsContainer({ settingId, tabId, mediaContainerElement, cachedFieldsValues }) {
  const {
    form: { control, getValues, setValue, clearErrors },
    callbacks,
  } = useContext(ElementSettingsContext);

  const [featureFlags] = useFeatureFlags(['yola_cms']);
  const isYolaCmsFeatureFlagEnabled = Boolean(featureFlags.yola_cms);

  const chunkId = chunkIds.LINK;
  const supportedLinkTypes = useMemo(
    () => getSupportedLinkTypes({ isBindingEnabled: isYolaCmsFeatureFlagEnabled }),
    [isYolaCmsFeatureFlagEnabled]
  );

  const linkConfig = useMemo(() => {
    const linkElement = mediaContainerElement.querySelector('a');

    return getLinkConfig(linkElement, mediaContainerElement);
  }, [mediaContainerElement]);

  const defaultValues = useMemo(
    () =>
      getDefaultValues({
        linkConfig,
        linkElement: mediaContainerElement.querySelector('a'),
        cachedFieldsValues,
      }),
    [linkConfig, mediaContainerElement, cachedFieldsValues]
  );

  const getFieldsValues = useCallback(() => getLinkFieldsValues(getValues), [getValues]);

  const handleFieldChange = useCallback(
    ({ fieldName, fieldValue } = {}) => {
      const fieldsValues = getFieldsValues();

      trackLinkSettingsFieldChanged({
        settingId,
        fieldName,
        fieldsValues,
      });
    },
    [settingId, getFieldsValues]
  );

  const handleFieldClick = useCallback(
    ({ fieldName }) => {
      const fieldsValues = getFieldsValues();

      trackLinkSettingsFieldChanged({
        settingId,
        fieldName,
        fieldsValues,
      });
    },
    [settingId, getFieldsValues]
  );

  const handleLinkTypeChange = useCallback(
    (newLinkType) => {
      const fieldsValues = getFieldsValues();

      trackLinkSettingsFieldChanged({
        settingId,
        fieldName: linkFieldNames.LINK_TYPE,
        fieldsValues,
      });
    },
    [settingId, getFieldsValues]
  );

  const handleToggleChange = useCallback(
    (value) => {
      callbacks.onToggleClick({
        settingId: settingIds.THIS_MEDIA,
        controlId: 'click-to-action',
        isActive: value,
      });

      if (value) {
        const fieldsValues = getFieldsValues();
        const linkElement = buildLinkElementFromFieldsValues(fieldsValues);

        mediaContainerBuilder.wrapContentWith(mediaContainerElement, linkElement);
      } else {
        mediaContainerBuilder.unwrapContent(mediaContainerElement);

        clearErrors(Object.values(linkFieldNames));
      }
    },
    [getFieldsValues, mediaContainerElement, callbacks, clearErrors]
  );

  const preProcessingHandler = useMemo(() => createSubmitPreProcessingLinkHandler(), []);

  // This chunk (LINK) does not have its own actions handler to avoid conflicts.
  // The actions handler is handled by the main chunk (IMAGE) which collects
  // field values from all related chunks and builds the complete markup structure.
  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    defaultValues,
    stageHandlers: [
      {
        stage: stages.SUBMIT_PRE_PROCESSING,
        handler: preProcessingHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) {
    return null;
  }

  return (
    <ToggleSection
      name={linkFieldNames.LINK_CLICK_ACTION}
      control={control}
      title={i18next.t('Click action')}
      onChange={handleToggleChange}
    >
      <LinkFieldContainer
        control={control}
        types={supportedLinkTypes}
        initialLinkConfig={linkConfig}
        menuPlacement="top"
        getFormValues={getValues}
        setFormValue={setValue}
        clearFormErrors={clearErrors}
        onLinkTypeChange={handleLinkTypeChange}
        onFieldChange={handleFieldChange}
        onFieldClick={handleFieldClick}
      />
    </ToggleSection>
  );
}

LinkSettingsContainer.propTypes = {
  settingId: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
  mediaContainerElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object])
    .isRequired,
  cachedFieldsValues: PropTypes.object,
};

export default LinkSettingsContainer;
